import { getSession, login, logout, LoginApiError, type LoginPrincipal } from '@urbanik/api-client';
import { TextField } from '@urbanik/ui';
import { useEffect, useState, type FormEvent } from 'react';
import { apiBase } from './api-base';
import { MandatoryPasswordChange } from './MandatoryPasswordChange';
type LoginState = 'idle' | 'loading' | 'invalid' | 'locked' | 'error' | 'success';
export function LoginView() {
  const params = new URLSearchParams(window.location.hash.split('?')[1]);
  const evidence = params.get('state');
  const initialState: LoginState =
    evidence && ['loading', 'invalid', 'locked', 'error'].includes(evidence)
      ? (evidence as LoginState)
      : 'idle';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState<LoginState>(initialState);
  const [principal, setPrincipal] = useState<LoginPrincipal | null>(null);
  const [retry, setRetry] = useState<number>();
  const [expired, setExpired] = useState(evidence === 'expired');
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  useEffect(() => {
    if (evidence === 'expired') {
      void logout(apiBase).finally(() => {
        setPrincipal(null);
        setState('idle');
        setExpired(true);
      });
      return;
    }
    if (!window.location.hash.startsWith('#session')) return;
    void getSession(apiBase)
      .then((result) => {
        setPrincipal(result.principal);
        setState('success');
        setPasswordChangeRequired(result.passwordChangeRequired);
        if (!result.passwordChangeRequired) window.location.hash = '#dashboard';
      })
      .catch(() => {
        setPrincipal(null);
        setState('idle');
        setExpired(true);
        window.location.hash = '#login?reason=expired';
      });
  }, [evidence]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (state === 'loading') return;
    setState('loading');
    try {
      const result = await login(apiBase, { email, password });
      setPrincipal(result.principal);
      setPassword('');
      setPasswordChangeRequired(result.passwordChangeRequired);
      setState('success');
      if (!result.passwordChangeRequired) window.location.hash = '#dashboard';
    } catch (error) {
      if (error instanceof LoginApiError) {
        setRetry(error.retryAfterSeconds);
        setState(
          error.kind === 'invalid' ? 'invalid' : error.kind === 'locked' ? 'locked' : 'error',
        );
      } else setState('error');
    }
  }
  return (
    <main className="login-page">
      <section className="login-brand" aria-label="URBANIK">
        <img src="/brand/urbanik-horizontal-dark-transparent.png" alt="URBANIK" />
        <p>Gestión comunitaria segura, clara y cercana.</p>
      </section>
      <section className="login-card" aria-labelledby="login-title">
        <p className="kicker">Acceso administrativo</p>
        <h1 id="login-title">Bienvenido de nuevo</h1>
        <p className="login-intro">Ingresa tus credenciales para verificar tu identidad.</p>
        {passwordChangeRequired ? (
          <MandatoryPasswordChange
            onComplete={() => {
              setPasswordChangeRequired(false);
              window.location.hash = '#dashboard';
            }}
          />
        ) : state === 'success' ? (
          <div className="login-result login-result--success" role="status">
            <strong>Identidad verificada</strong>
            <span>{principal?.displayName}</span>
            <small>Sesión activa y recuperable de forma segura.</small>
            <a className="ui-button ui-button--primary" href="#dashboard">
              Continuar a la aplicación
            </a>
            <button
              className="ui-button ui-button--secondary"
              type="button"
              onClick={() =>
                void logout(apiBase).then(() => {
                  setPrincipal(null);
                  setState('idle');
                  setExpired(false);
                  window.location.hash = '#login';
                })
              }
            >
              Cerrar sesión
            </button>
          </div>
        ) : (
          <form noValidate onSubmit={(event) => void submit(event)} aria-busy={state === 'loading'}>
            {expired ? (
              <div className="login-alert login-alert--expired" role="alert">
                <strong>Tu sesión terminó</strong>
                <span>Por seguridad, ingresa nuevamente para continuar.</span>
              </div>
            ) : null}
            {state === 'invalid' ? (
              <div className="login-alert" role="alert">
                Las credenciales no son válidas.
              </div>
            ) : null}
            {state === 'locked' ? (
              <div className="login-alert" role="alert">
                Acceso bloqueado temporalmente.{' '}
                {retry
                  ? `Intenta en ${Math.ceil(retry / 60)} minutos.`
                  : 'Intenta nuevamente más tarde.'}
              </div>
            ) : null}
            {state === 'error' ? (
              <div className="login-alert" role="alert">
                No pudimos conectar con el servidor. Intenta nuevamente.
              </div>
            ) : null}
            <TextField
              id="login-email"
              label="Correo electrónico o cédula"
              type="text"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={state === 'loading'}
            />
            <div className="login-password">
              <TextField
                id="login-password"
                label="Contraseña"
                type={visible ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={state === 'loading'}
              />
              <button
                type="button"
                onClick={() => setVisible((value) => !value)}
                aria-pressed={visible}
              >
                {visible ? 'Ocultar' : 'Mostrar'} contraseña
              </button>
            </div>
            <button
              className="ui-button ui-button--primary login-submit"
              type="submit"
              disabled={state === 'loading'}
            >
              {state === 'loading' ? 'Verificando…' : 'Ingresar'}
            </button>
          </form>
        )}
        <footer>URBANIK · America/Guayaquil</footer>
      </section>
    </main>
  );
}
